import React, { useEffect, useMemo } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { useSearchParams, Link } from "react-router-dom";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import { useTablePagination, useAppMeta } from "hooks";
import { useCustomParams } from "hooks/useCustomParams";
import { useLazyListBSLViewQuery } from "store/services/BranchShipmentLocationApi";
import BSLReportViewFilter from "./BranchShipmentLocationComponents/BSLReportViewFilter";
import moment from "moment";

const TYPE_CHIP = {
  source: { label: "Source", bg: "#EDE7F6", color: "#4527A0" },
  transit: { label: "Transit", bg: "#FFF3CD", color: "#856404" },
  destination: { label: "Destination", bg: "#D1ECF1", color: "#0C5460" },
};

const BSLReportViewScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();
  const { params } = useCustomParams();

  const keyword = searchParams.get("search") || "";
  const branch = params.branch || searchParams.get("branch") || "";
  const fromDate = params.fromDate || "";
  const toDate = params.toDate || "";
  const type = params.type || "";

  const [fetchBSLView, { data: bslViewRes, isLoading, isFetching, error }] =
    useLazyListBSLViewQuery();

  useEffect(() => {
    fetchBSLView({
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(branch && { branch }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(type && { type }),
    });
  }, [currentPage, perPage, keyword, branch, fromDate, toDate, type, fetchBSLView]);

  useAppMeta("BSL Shipment View");

  const columns = useMemo(
    () => [
      {
        name: "Shipment ID",
        selector: (row) => row.shipmentId || "N/A",
        cell: (row) => (
          <Link
            to={`/app/crm/shipment/${row.shipment?._id || row.shipment}/detail`}
            style={{ textDecoration: "none" }}
          >
            <HighlightedText
              text={row.shipmentId || "N/A"}
              highlight={keyword}
            />
          </Link>
        ),
      },
      {
        name: "Current Branch",
        selector: (row) => row.currentBranch?.name || "N/A",
        cell: (row) => (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {row.currentBranch?.name || "N/A"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.currentBranch?.code || ""}
            </Typography>
          </Box>
        ),
      },
      {
        name: "Destination Branch",
        selector: (row) => row.destinationBranch?.name || "N/A",
        cell: (row) => (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {row.destinationBranch?.name || "N/A"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.destinationBranch?.code || ""}
            </Typography>
          </Box>
        ),
      },
      {
        name: "Type",
        selector: (row) => row.type || "",
        cell: (row) => {
          const cfg = TYPE_CHIP[row.type] || {};
          return (
            <Chip
              label={cfg.label || row.type || "N/A"}
              size="small"
              sx={{
                backgroundColor: cfg.bg || "#eee",
                color: cfg.color || "#333",
                fontWeight: "bold",
                height: "22px",
                borderRadius: "8px",
                "& .MuiChip-label": { fontSize: "0.72rem", padding: "0 8px" },
              }}
            />
          );
        },
        center: true,
      },
      {
        name: "Status",
        selector: (row) => row.shipmentStatus || "N/A",
        cell: (row) => (
          <Typography variant="body2">{row.shipmentStatus || "N/A"}</Typography>
        ),
      },

      {
        name: "Added By",
        selector: (row) => row.addedBy?.name || "N/A",
        cell: (row) => (
          <Typography variant="body2">{row.addedBy?.name || "N/A"}</Typography>
        ),
      },
      {
        name: "Added At",
        selector: (row) =>
          row.addedAt ? moment(row.addedAt).format("YYYY-MM-DD HH:mm") : "N/A",
        cell: (row) => (
          <Tooltip
            title={
              row.addedAt
                ? moment(row.addedAt).format("DD MMM YYYY, hh:mm A")
                : ""
            }
          >
            <Typography variant="body2" sx={{ cursor: "default" }}>
              {row.addedAt
                ? moment(row.addedAt).format("DD MMM YY, HH:mm")
                : "N/A"}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    [keyword],
  );

  return (
    <BreadcrumbLayout
      breadcrumbs={[
        { title: "Reports", path: "/app/crm/reports/list" },
        {
          title: "BSL Report",
          path: "/app/crm/reports/branch-shipment-location-report",
        },
        { title: "Shipment View" },
      ]}
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BSLReportViewFilter disabled={isLoading || isFetching} />

        <AppTable
          columns={columns}
          data={bslViewRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={bslViewRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default BSLReportViewScreen;